import React, { useState, useEffect, useContext } from 'react';
import _ from 'lodash';
import { AdminService } from './AdminService';
import { PageContext } from '../pages/components/page';
import { hasPermissions } from '../site/userService';
import { NavTab, TabContent, TabPanel, Tabset, TabList } from '../components/tabs/Tabs';
import { spinnerHide, spinnerShow } from '../lib/spinner';
import { Modal, ModalProps } from '../components/modal/Modal';
import { setHelpPage } from '../configTdm';
import SearchTextComponent from './SearchText';

const ConfirmModal: React.FC<ModalProps> = ({ isOpen, onClose, onSubmit, title, children }) => (
    <Modal
        isOpen={isOpen}
        onClose={onClose}
        onSubmit={onSubmit}
        title={title}
        okButtonText='Yes'
        cancelButtonText='No'
    >
        {children}
    </Modal>
);

interface Account {
    id: string;
    account: {
        name: string;
        roles: string[];
    };
    selected?: boolean;
    hidden?: boolean;
    entitlements?: {
        accountId: string;
        nodes: Array<{
            id: string;
            name: string;
            type: string;
            permissions: string[];
        }>;
    };
}

interface Session {
    id: string;
    accountId: string;
    uname: string;
    cookie: {
        expires: string;
    };
    selected?: boolean;
    hidden?: boolean;
}

const AdminComponent: React.FC = () => {
    const { pushAlert } = useContext(PageContext);
    const [activeTab, setActiveTab] = useState('accounts');
    const [accounts, setAccounts] = useState<Account[]>([]);
    const [sessions, setSessions] = useState<Session[]>([]);
    const [isAllSelected, setIsAllSelected] = useState({ accounts: false, sessions: false });
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmModalMsg, setConfirmModalMsg] = useState('Are you sure you want to delete this session?');
    const [refresh, setRefresh] = useState<boolean>(false);

    useEffect(() => {
        // spinnerShow();
        setHelpPage('helpAdmin');
        (async () => {
            await refreshAccounts();
            await refreshSessions();
            // spinnerHide();
        })();
    }, []);

    const hasDeletePermissions = (perms: any) => hasPermissions(perms);

    const checkStateChange = (coll: any[], selected: boolean) => {
        _.forEach(coll, item => {
            item.selected = selected;
        });
    };

    const selectedItems = (coll: any[]) => coll.filter(item => item.selected);

    const isButtonDisabled = (dataName: string, buttonName: string) => {
        let coll = null;
        switch (dataName.toLowerCase()) {
            case 'accounts':
                coll = accounts;
                break;
            case 'sessions':
                coll = sessions;
                break;
            default:
                coll = null;
        }
        if (coll) {
            const selected = selectedItems(coll);
            switch (buttonName) {
                case 'edit':
                    return selected.length !== 1;
                case 'delete':
                    return selected.length < 1;
                default:
                    return false;
            }
        }
        return false;
    };

    const refreshAccounts = async () => {
        try {
            const resp = await AdminService.getAccounts();
            if (resp.status === 200) {
                const accountsData = _.isArray(resp.data) ? resp.data : [];
                setAccounts(accountsData);
                const acctMap = _.keyBy(accountsData, 'id');
                _.forOwn(acctMap, async acct => {
                    const resp = await AdminService.getAccountEntitlements(acct.id);
                    if (resp.status === 200 && resp.data) {
                        const data = resp.data;
                        if (data?.accountId && data.nodes) {
                            acctMap[data.accountId].entitlements = data;
                        }
                    } else {
                        pushAlert('danger', 'Unable to retrieve entitlements');
                    }
                });
            } else {
                pushAlert('danger', 'Error refreshing accounts.');
            }
        } catch (error) {
            pushAlert('danger', 'Error refreshing accounts.');
        }
    };

    const refreshSessions = async () => {
        try {
            const resp = await AdminService.getSessions();
            if (resp.status === 200) {
                const sessionsData = _.isPlainObject(resp.data)
                    ? Object.keys(resp.data).map(key => ({ ...resp.data[key], id: key }))
                    : [];
                setSessions(sessionsData);
            } else {
                pushAlert('danger', 'Error refreshing sessions.');
            }
        } catch (error) {
            pushAlert('danger', 'Error refreshing sessions.');
        }
    };

    const handleRefresh = async (dataName: string, flag: boolean) => {
        // spinnerShow();
        switch (dataName.toLowerCase()) {
            case 'accounts':
                await refreshAccounts();
                break;
            case 'sessions':
                await refreshSessions();
                break;
            default:
                break;
        }
        setRefresh(flag ?? false);
        // spinnerHide();
    }

    const handleDelete = async () => {
        const selItems = selectedItems(sessions);
        const totalSelected = selItems.length;
        if (totalSelected > 0) {
            const msg =
                totalSelected > 1
                    ? 'Are you sure you want to delete these sessions?'
                    : 'Are you sure you want to delete this session?';
            setConfirmModalMsg(msg);
            setShowConfirmModal(true);
        }
    };

    const deleteSession = async () => {
        try {
            // spinnerShow();
            const selItems = selectedItems(sessions);
            const del = { sessions: selItems.map(item => item.id) };
            await AdminService.deleteSessions(del);
            await refreshSessions();
        } catch (error) {
            pushAlert('danger', 'Error deleting sessions.');
        } finally {
            // spinnerHide();
        }
    }

    return (
        <div className='content'>
            <div className="admin">
                <h2>Admin</h2>
                <p>This is where you can view and modify users and accounts.</p>
                <Tabset>
                    <TabList className='nav-tabs'>
                        <NavTab
                            name={`Accounts (${accounts.length})`}
                            active={activeTab === 'accounts'}
                            onSelect={() => {
                                setActiveTab('accounts');
                            }}
                        />
                        <NavTab
                            name={`Sessions (${sessions.length})`}
                            active={activeTab === 'sessions'}
                            onSelect={() => {
                                setActiveTab('sessions');
                            }}
                        />
                    </TabList>
                    <TabContent>
                        <TabPanel active={activeTab === 'accounts'}>
                            <div className="panel panel-default">
                                <div className="panel-body">
                                    <SearchTextComponent listData={accounts} callback={(listData: Account[]) => {
                                        setAccounts([...listData]);
                                    }} refresh={refresh} updateRefresh={() => setRefresh(false)}></SearchTextComponent>
                                    <table
                                        id="accountsGrid"
                                        className="table table-striped table-hover table-condensed table-bordered"
                                    >
                                        <colgroup>
                                            <col className="col1" />
                                            <col className="col2" />
                                            <col className="col3" />
                                            <col className="col4" />
                                        </colgroup>
                                        <thead>
                                            <tr>
                                                <th>
                                                    <input
                                                        type="checkbox"
                                                        checked={isAllSelected.accounts}
                                                        onChange={e => {
                                                            setIsAllSelected({ ...isAllSelected, accounts: e.target.checked });
                                                            checkStateChange(accounts, e.target.checked);
                                                        }}
                                                    />
                                                </th>
                                                <th>ID</th>
                                                <th>Name</th>
                                                <th>Roles</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {accounts.map(account => (
                                                !account.hidden && (
                                                    <tr key={account.id}>
                                                        <td>
                                                            <input
                                                                type="checkbox"
                                                                checked={account.selected}
                                                                onChange={e => {
                                                                    account.selected = e.target.checked;
                                                                    setAccounts([...accounts]);
                                                                }}
                                                            />
                                                        </td>
                                                        <td data-fs-mask className="fs-mask">{account.id}</td>
                                                        <td data-fs-mask className="fs-mask">{account.account.name}</td>
                                                        <td>{account.account.roles}</td>
                                                        <td>
                                                            <a href={`/admin/accounts/${account.id}`} data-fs-exclude className="fs-exclude">Show</a>
                                                        </td>
                                                    </tr>
                                                )
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="panel-footer">
                                    <button type="button" className="btn btn-primary" onClick={() => handleRefresh('accounts', true)}>
                                        Refresh
                                    </button>
                                </div>
                            </div>
                        </TabPanel>
                        <TabPanel active={activeTab === 'sessions'}>
                            <div className="panel panel-default">
                                <div className="panel-body">
                                    <SearchTextComponent listData={sessions} callback={(listData: Session[]) => {
                                        setSessions([...listData]);
                                    }} refresh={refresh} updateRefresh={() => setRefresh(false)}></SearchTextComponent>
                                    <table
                                        id="sessionsGrid"
                                        className="table table-striped table-hover table-condensed table-bordered"
                                    >
                                        <colgroup>
                                            <col className="col1" />
                                            <col className="col2" />
                                            <col className="col3" />
                                        </colgroup>
                                        <thead>
                                            <tr>
                                                <th>
                                                    <input
                                                        type="checkbox"
                                                        checked={isAllSelected.sessions}
                                                        onChange={e => {
                                                            setIsAllSelected({ ...isAllSelected, sessions: e.target.checked });
                                                            checkStateChange(sessions, e.target.checked);
                                                        }}
                                                    />
                                                </th>
                                                <th>ID</th>
                                                <th>Account</th>
                                                <th>User name</th>
                                                <th>Expires</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sessions.map(session => (
                                                !session.hidden && (
                                                    <tr key={session.id}>
                                                        <td>
                                                            <input
                                                                type="checkbox"
                                                                checked={session.selected}
                                                                onChange={e => {
                                                                    session.selected = e.target.checked;
                                                                    setSessions([...sessions]);
                                                                }}
                                                            />
                                                        </td>
                                                        <td data-fs-exclude className="fs-exclude">{session.id}</td>
                                                        <td data-fs-exclude className="fs-exclude">{session.accountId}</td>
                                                        <td data-fs-exclude className="fs-exclude">{session.uname}</td>
                                                        <td>{session.cookie.expires}</td>
                                                    </tr>
                                                )
                                            ))}
                                        </tbody>
                                    </table>
                                    <div className="panel-footer">
                                        <button type="button" className="btn btn-primary" onClick={() => handleRefresh('sessions', true)}>
                                            Refresh
                                        </button>
                                        {hasDeletePermissions('delete-sessions') && <button
                                            type="button"
                                            className="btn btn-danger"
                                            onClick={handleDelete}
                                            disabled={isButtonDisabled('sessions', 'delete')}
                                        >
                                            Delete
                                        </button>}
                                    </div>
                                </div>
                            </div>
                        </TabPanel>
                    </TabContent>
                </Tabset>
            </div>
            <ConfirmModal
                isOpen={showConfirmModal}
                onClose={() => setShowConfirmModal(false)}
                onSubmit={() => {
                    deleteSession();
                    setShowConfirmModal(false);
                }}
                title={'Confirm'}
                children={confirmModalMsg}
            />
        </div>
    );
};

export default AdminComponent;